/**
 * SlotPicker — color-slot selection widget used in both the
 * unified LOBBY screen (State B, non-player viewer) and the
 * /(app)/join/[code] redirector.
 *
 * Accepts the same slot-state shape produced by deriveSlotState.
 */

import React from 'react'
import { View, Text, TouchableOpacity, useWindowDimensions } from 'react-native'
import { PLAYER_COLORS, LOCKED_PLAYER_SLOTS, clampPlayerSlot, PlayerColorChip } from '@warcarp/ui'
import type { TeamId } from '@warcarp/api-types'
import { playerChipRim, playerColorSlot } from '../../lib/playerColor'
import { useTheme } from '../../hooks/useTheme'
import { TeamShape } from './TeamShape'

// ─── deriveSlotState ─────────────────────────────────────────────────────────

/**
 * Derive which slots are available and which are taken given the server payload.
 * availableSlots is the authoritative list from the server.
 * Players array provides display names for taken slots.
 *
 * Accepts either PreviewGameResponse['players'] or a minimal shape with
 * { slot: number; displayName: string } so it works with both preview and
 * full GameResponse player lists.
 */
export function deriveSlotState(
  maxPlayers: number,
  availableSlots: number[],
  players: Array<{ slot: number; displayName: string }>,
): Array<{
  slot: number
  available: boolean
  takenBy: string | null
}> {
  const available = new Set(availableSlots)
  const bySlot: Record<number, string> = {}
  for (const p of players) {
    bySlot[p.slot] = p.displayName
  }
  return Array.from({ length: maxPlayers }, (_, i) => {
    const slot = i + 1
    return {
      slot,
      available: available.has(slot),
      takenBy: bySlot[slot] ?? null,
    }
  })
}

// ─── SlotPicker (color picker) ───────────────────────────────────────────────

/**
 * Color chooser shown to a non-player viewer before they join. Color is
 * DECOUPLED from seat: all 9 palette colors are always offered regardless of
 * how many seats the game has; a color is disabled only when another player
 * already holds it. The seat itself is auto-assigned by the server on join.
 */
export interface SlotPickerProps {
  /** Players already in the game — used to mark colors as taken (by colorSlot). */
  players: Array<{ colorSlot?: number | null; slot: number; displayName: string }>
  selectedColor: number | null
  onSelect: (color: number) => void
  /** Heading above the swatches (default "Pick a Color"). */
  label?: string
  /** Section headings match the account settings cards. */
  headingVariant?: 'compact' | 'section'
  /** Optional "no color" choice (e.g. clearing a default color), shown at the
   *  right of the heading; selected while `selectedColor` is null. */
  noneOption?: { label: string; onSelect: () => void }
  /** Grid columns. Defaults to three at all widths; swatches are always equal
   *  width and span the full area. */
  columns?: number
  /** Shown beside the heading, e.g. a
   *  transient "Saved" confirmation. */
  selectedAccessory?: React.ReactNode
  /** Screen-reader word for the selected color, e.g. "current" announces
   *  "Harvest — your current color" (Change Color). */
  selectedNote?: string
  /**
   * Pick seats team lobbies: the picker's team. Each swatch is drawn in the
   * team's shape so the player sees their colour as it will look. Null (or
   * omitted) keeps circles.
   */
  team?: TeamId | null
}

/** Nine short palette names fit a consistent three-by-three grid. */
export function slotPickerColumns(_windowWidth: number): number {
  return 3
}

export function SlotPicker({ players, selectedColor, onSelect, label = 'Pick a Color', headingVariant = 'compact', noneOption, columns, selectedNote, selectedAccessory, team }: SlotPickerProps) {
  const { width } = useWindowDimensions()
  const gridColumns = columns ?? slotPickerColumns(width)
  const compact = gridColumns >= 3
  // color slot → the player holding it (if any).
  // Dark colours (Raven) vanish on dark tiles: a light rim in dark mode.
  const { theme } = useTheme()
  const chipRim = playerChipRim(theme)
  const holderByColor = new Map<number, { displayName: string }>()
  for (const p of players) holderByColor.set(playerColorSlot(p), p)

  return (
    <View className="gap-2">
      {/* An empty label (e.g. inside a titled popup) drops the heading row. */}
      {(label || selectedAccessory || noneOption) ? <View className="flex-row items-center justify-between gap-2">
        {label ? <Text className={`font-stencil uppercase tracking-widest flex-shrink ${headingVariant === 'section' ? 'text-olive-up text-xs' : 'text-ink-soft text-[10px]'}`}>
          {label}
        </Text> : null}
        {selectedAccessory}
        {noneOption && (
          <TouchableOpacity
            onPress={noneOption.onSelect}
            accessibilityLabel={noneOption.label}
            accessibilityRole="button"
            accessibilityState={{ selected: selectedColor === null }}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            className={`flex-row items-center gap-1 rounded-full px-2.5 py-1 border ${
              selectedColor === null ? 'bg-paper border-ink' : 'border-olive'
            }`}
          >
            <Text className={`font-typed text-xs ${selectedColor === null ? 'text-ink' : 'text-ink-soft'}`}>{noneOption.label}</Text>
            {selectedColor === null && <Text className="text-ink font-stencil text-[10px]">✓</Text>}
          </TouchableOpacity>
        )}
      </View> : null}
      <View className="flex-row flex-wrap" style={{ margin: -4 }}>
        {LOCKED_PLAYER_SLOTS.map((color) => {
          const clamped = clampPlayerSlot(color)
          const entry = PLAYER_COLORS[clamped]
          const takenBy = holderByColor.get(color)?.displayName ?? null
          const available = takenBy === null
          const isSelected = selectedColor === color

          const swatch = (
            <TouchableOpacity
              key={color}
              onPress={() => available && onSelect(color)}
              accessibilityLabel={
                isSelected && selectedNote
                  ? `${entry.name} — your ${selectedNote} color`
                  : available
                    ? `Pick ${entry.name}`
                    : `${entry.name} taken${takenBy ? ` by ${takenBy}` : ''}`
              }
              accessibilityRole="button"
              accessibilityState={{ selected: isSelected, disabled: !available }}
              disabled={!available}
              className={`flex-row items-center rounded-xl py-2.5 border-2 ${compact ? 'gap-1 px-2' : 'gap-2 px-3'} ${
                isSelected
                  ? 'bg-paper border-ink'
                  : available
                    ? 'bg-paper-up border-olive'
                    : 'bg-paper-up border-olive opacity-40'
              }`}
            >
              {team ? (
                <TeamShape team={team} size={20} fill={entry.hex}
                  stroke={chipRim?.borderColor ?? 'rgba(0,0,0,0.15)'} strokeWidth={chipRim?.borderWidth ?? 1}
                  testID={`slot-picker-shape-${color}`} />
              ) : (
                <PlayerColorChip slot={clamped} size="sm" style={chipRim} />
              )}
              <View className="flex-1">
                <Text
                  className={`font-typed ${compact ? 'text-[11px]' : 'text-xs'} ${available ? 'text-ink' : 'text-ink-soft line-through'}`}
                  numberOfLines={1}
                >
                  {entry.name}
                </Text>
                {/* No second line (holder's name, "current"): every box stays the
                    same height. The roster shows who holds what; screen readers
                    get it from the label, and ✓ marks the selection. */}
              </View>
              {isSelected && (
                // A plain check, large and bold enough to read at a glance.
                <View pointerEvents="none" style={compact ? { position: 'absolute', right: 8, top: 0, bottom: 0, justifyContent: 'center' } : undefined}>
                  <Text testID="slot-picker-check" className="text-ink" style={{ fontSize: 18, lineHeight: 20, fontWeight: '800' }}>✓</Text>
                </View>
              )}
            </TouchableOpacity>
          )
          return <View key={color} style={{ width: `${100 / gridColumns}%`, padding: 4 }}>{swatch}</View>
        })}
      </View>
    </View>
  )
}
